import { CardLoadingSpinner } from "components/loading/CardLoadingSpinner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { Info } from "lucide-react";
import { Controller } from "react-hook-form";
import { ACCESS_REQUEST_MESSAGES } from "../../data/messages";
import { useAccessRequestValidation } from "../../ducks/filter/hooks/use-access-request-validation";
import { Banner } from "./banner-information";

interface UserCardProps {
  formMethods: any;
  onValidationChange?: (canSubmit: boolean) => void;
}

export const UserCard = ({
  formMethods,
  onValidationChange,
}: UserCardProps) => {
  const {
    register,
    control,
    formState: { errors },
  } = formMethods;

  const { cpfMask, handleCpfChange, isCpfValid, banner, isLoading } =
    useAccessRequestValidation({ formMethods, onValidationChange });

  return (
    <Card className="relative">
      {isLoading && (<CardLoadingSpinner />)}

      <CardHeader>
        <CardTitle>{ACCESS_REQUEST_MESSAGES.CARD.TITLE}</CardTitle>
        <CardDescription>
          {ACCESS_REQUEST_MESSAGES.CARD.DESCRIPTION}
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        <Banner
          variant={banner.variant}
          title={banner.title}
          message={banner.message}
        />

        <form className="space-y-4">
          <div className="flex gap-6">
            <div className="flex gap-3 w-1/2">
              <div className="flex-1 flex flex-col gap-1">
                <Label htmlFor="cpf" className="flex items-center gap-2">
                  {ACCESS_REQUEST_MESSAGES.LABELS.CPF}
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="cursor-pointer" />
                    </TooltipTrigger>
                    <TooltipContent className="bg-black bg-opacity-75 text-white p-1 rounded shadow-lg">
                      <div className="relative">
                        <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-3 h-3 bg-black bg-opacity-75 rotate-45"></div>
                        {ACCESS_REQUEST_MESSAGES.TOOLTIP.CPF}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </Label>

                <Controller
                  name="cpf"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={cpfMask}
                      hasError={
                        !!errors.cpf || (!isCpfValid && cpfMask.length > 5)
                      }
                      onChange={(e) => {
                        field.onChange(e);
                        handleCpfChange(e);
                      }}
                      placeholder={ACCESS_REQUEST_MESSAGES.PLACEHOLDERS.CPF}
                    />
                  )}
                />

                {errors.cpf && (
                  <p className="text-red-500 text-sm">{errors.cpf.message}</p>
                )}
                {!isCpfValid && cpfMask.length > 5 && (
                  <p className="text-red-500 text-sm">
                    {ACCESS_REQUEST_MESSAGES.VALIDATION.INVALID_CPF}
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-3 w-1/2">
              <div className="flex-1 flex flex-col gap-1">
                <Label htmlFor="name">
                  {ACCESS_REQUEST_MESSAGES.LABELS.NAME}
                </Label>
                <Input
                  id="name"
                  type="text"
                  disabled
                  placeholder={ACCESS_REQUEST_MESSAGES.PLACEHOLDERS.NAME}
                  {...register("name")}
                />
                {errors.name && (
                  <p className="text-red-500 text-sm">{errors.name.message}</p>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-6">
            <div className="flex gap-3 w-1/2">
              <div className="flex-1 flex flex-col gap-1">
                <Label htmlFor="register">
                  {ACCESS_REQUEST_MESSAGES.LABELS.REGISTER}
                </Label>
                <Input
                  id="register"
                  type="text"
                  disabled
                  placeholder={ACCESS_REQUEST_MESSAGES.PLACEHOLDERS.REGISTER}
                  {...register("matricula")}
                />
                {errors.register && (
                  <p className="text-red-500 text-sm">
                    {errors.register.message}
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-3 w-1/2">
              <div className="flex-1 flex flex-col gap-1">
                <Label htmlFor="position">
                  {ACCESS_REQUEST_MESSAGES.LABELS.POSITION}
                </Label>
                <Input
                  id="position"
                  type="text"
                  disabled
                  placeholder={ACCESS_REQUEST_MESSAGES.PLACEHOLDERS.POSITION}
                  {...register("cargo")}
                />
                {errors.position && (
                  <p className="text-red-500 text-sm">
                    {errors.position.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-6">
            <div className="flex gap-3 w-1/2">
              <div className="flex-1 flex flex-col gap-1">
                <Label htmlFor="situation">
                  {ACCESS_REQUEST_MESSAGES.LABELS.SITUATION}
                </Label>
                <Input
                  id="situation"
                  type="text"
                  disabled
                  placeholder=""
                  {...register("situation")}
                />
                {errors.situation && (
                  <p className="text-red-500 text-sm">
                    {errors.situation.message}
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-3 w-1/2">
              <div className="flex-1 flex flex-col gap-1">
                <Label htmlFor="cost_center">
                  {ACCESS_REQUEST_MESSAGES.LABELS.COST_CENTER}
                </Label>
                <Input
                  id="cost_center"
                  type="text"
                  disabled
                  placeholder=""
                  {...register("cost_center")}
                />
                {errors.cost_center && (
                  <p className="text-red-500 text-sm">
                    {errors.cost_center.message}
                  </p>
                )}
              </div>
            </div>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};